import { escapeRegExp } from 'lodash'
import React, {
  FC,
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import { Column, Renderer, CellProps, PluginHook } from 'react-table'
import { Signal } from '@bdswiss/mt4-connector/build/main/mt4-types'
import {MT4Connector, PricesExtended, TradeObject} from '@bdswiss/mt4-connector'
import { Translate, I18n } from 'react-redux-i18n'
import { VirtualizedTable } from './VirtualizedTable'
import { store } from '../index'
import { setTradingSignal } from '../actions/commonActions'
import { SignalPositionsTableContentContainer } from '../containers/SignalPositionsTable'
import { TableContext, expandedReducer } from './contexts/TableContext'
import { VariableSizeList } from 'react-window'
import { toast } from 'react-toastify'
import { humanReadableResolution } from '../lib/charting_library/utils'
import {SimpleSignal} from "../types";
import moment from "moment-timezone";

const FavouriteCell: Renderer<CellProps<
  Signal,
  { favourite: boolean; confirmed: boolean; symbol: string }
>> = ({ value: { favourite, confirmed, symbol } }) => {
  const { onToggleFavourite } = useContext(TableContext)
  return (
    <i
      onClick={() => onToggleFavourite!(symbol)}
      className={`favourite ${favourite ? 'icon-star-full' : 'icon-star-empty'} ${
        !confirmed ? 'unconfirmed' : ''
      }`}
      title={I18n.t('favourite')}
      // data-cy={props.value}
    />
  )
}

const SymbolCell: Renderer<CellProps<Signal>> = ({ value, row }) => {
  const setSignal = useCallback(() => {
    store.dispatch(setTradingSignal(row.original))
  }, [row])
  if (!value) {
    return <div />
  }


  // @ts-ignore
  const txt = Object.values(MT4Connector.Instance.assets as any).find(x => x.symbol === value)?.label ?? value
  return (
    <div onClick={setSignal} className="bdswiss-table--bold" title={txt} data-cy={txt}>
      {txt}
    </div>
  )
}

const DirectionCell: Renderer<CellProps<any>> = ({ value: { direction }, row }) => {
  direction = direction == 1 ? 'up' : 'down'
  const setSignal = useCallback(() => {
    store.dispatch(setTradingSignal(row.original))
  }, [row])
  return (
    <div
      className={/up/i.test(direction) ? 'bdswiss-table--positive' : 'bdswiss-table--negative'}
      onClick={setSignal}
      title={direction}
    >
      <i className={/up/i.test(direction) ? 'icon-buy-new' : 'icon-sell-new'} />
      <Translate value={direction} />
    </div>
  )
}


const MessageCell: Renderer<CellProps<any>> = ({ value: { message }, row }) => {
  const setSignal = useCallback(() => {
    store.dispatch(setTradingSignal(row.original))
  }, [row])
  return <div onClick={setSignal}>{message}</div>
}

const DateCell: Renderer<CellProps<any>> = ({ value: { createdAt, timeFrameStart }, row }) => {
  const setSignal = useCallback(() => {
    store.dispatch(setTradingSignal(row.original))
  }, [row])
  const date = timeFrameStart ?? createdAt
  const txt = moment(date).calendar()
  return <div onClick={setSignal}>{txt}</div>
}


const IdCell: Renderer<CellProps<any, string>> = ({ value }) => {
  const copyToClipboard = useCallback<MouseEventHandler>(
    e => {
      const textField = document.createElement('textarea')
      textField.innerText = value
      const parentElement = e.currentTarget
      parentElement!.appendChild(textField)
      textField.select()
      document.execCommand('copy')
      parentElement!.removeChild(textField)
      toast('Id copied to clipboard')
    },
    [value]
  )
  return <div onClick={copyToClipboard}>{value}</div>
}

const showMustGoOn = document.cookie
  .split(';')
  .map(c => c.trim())
  .filter(value => value.startsWith('showMustGoOn=true')).length

const initialState: { [signalId: string]: boolean } = {}
export interface SignalsTableProps {
  signals: { [key: string]: SimpleSignal }
  signal: SimpleSignal
  assets: PricesExtended
  favourites: Map<string, { confirmed: boolean; operation: 'on' | 'off' }>
  filter: string
  positions: TradeObject[]
  onToggleFavourite: (favourite: string) => void
}

const plugins: Array<PluginHook<{}>> = [
  hooks => {
    hooks.getCellProps.push((props, meta) => {
      if (meta.cell.column.id === 'favourite') {
        return { className: 'rt-td favourite-cell' }
      } else {
        return { className: 'rt-td' }
      }
    })
  },
]

export const SimpleSignalsTable: FC<SignalsTableProps> = ({
  signals,
  signal,
  favourites,
  filter,
  positions,
  onToggleFavourite,
}) => {
  const columns = useMemo<Array<Column<SimpleSignal>>>(() => {
    const newVar: Array<Column<SimpleSignal>> = [
      {
        Header: ' ',
        id: 'favourite',
        minWidth: 50,
        width: 50,
        accessor: ({ symbol }) => {
          const favourite = favourites.get(symbol)
          const confirmed = favourite ? favourite.confirmed : true
          return { favourite, confirmed, symbol }
        },
        Cell: FavouriteCell,
      },
      {
        Header: 'asset',
        id: 'symbol',
        accessor: ({ symbol }) => symbol,
        minWidth: 30,
        width: 60,

        Cell: SymbolCell,
      },
      {
        Header: 'direction',
        id: 'direction',
        accessor: ({ id, direction }) => ({ id, direction }),
        minWidth: 30,
        width: 60,

        Cell: DirectionCell,
      },

      {
        Header: 'Message',
        minWidth: 40,
        accessor: ({ id, message }) => ({ id, message }),
        Cell: MessageCell,
      },
      {
        Header: 'Date',
        minWidth: 40,
        accessor: ({ timeFrameStart, createdAt }) => ({ timeFrameStart, createdAt }),
        Cell: DateCell,
      }
    ]
    if (showMustGoOn) {
      newVar.push({
        Header: 'id',
        // minWidth: 40,
        accessor: 'id',
        Cell: IdCell,
      })
    }
    return newVar
  }, [favourites])
  const data = useMemo(() => {
    return filter
      ? Object.values(signals)
          .filter(({ symbol, direction, }) =>
            new RegExp(escapeRegExp(filter.replace('/', '')), 'i').test(
              [
                symbol,
                direction,
                // pattern ? 'Chart Pattern' : 'Key Level',
                // pattern ? pattern.name : keylevel!.type,
              ].join('|')
            )
          )
          .sort((a, b) => (a.symbol < b.symbol ? -1 : 1))
      : Object.values(signals).sort((a, b) => (a.symbol < b.symbol ? -1 : 1))
  }, [signals, filter])

  const [expandedPositionsState, expandedPositionsDispatch] = useReducer(
    expandedReducer,
    initialState
  )
  const getItemSize = useCallback(
    (index: number) => {
      const signal = data[index]
      // const positionsSize = positions.filter(p => p.signalId === signal.id).length
      const positionsSize = 0
      if (positionsSize === 0) {
        return 36
      }
      if (positionsSize <= 3) {
        return 36 + 22 + positionsSize * 36
      }
      return 36 + 22 + (expandedPositionsState[signal.id] ? positionsSize : 3) * 36 + 22
    },
    [data, positions, expandedPositionsState]
  )
  const listRef = useRef<VariableSizeList>(null)
  useEffect(() => {
    if (listRef && listRef.current) {
      listRef.current.resetAfterIndex(0)
    }
  }, [listRef, positions.length, data.length])
  const signalId = signal && signal.id
  const isRowActive = useCallback(id => id === signalId, [signalId])

  return (
    <TableContext.Provider
      value={{
        keyPropertyName: 'id',
        onToggleFavourite,
        expandedPositionsDispatch,
        expandedPositionsState,
        listRef,
        isRowActive,
      }}
    >
      <VirtualizedTable
        key={`autoSizer_${filter}`}
        columns={columns as Column<{}>[]}
        data={data}
        SubComponent={SignalPositionsTableContentContainer}
        getItemSize={getItemSize}
        plugins={plugins}
      />
    </TableContext.Provider>
  )
}

